import { useEffect, type ReactNode } from 'react'
import { Alert, Button, Loader, Paper } from '@mantine/core'
import { logError } from '@/utils/errorUtils'
import { InfoIcon } from './InfoIcon'

type QueryState = {
  isPending: boolean
  error: Error | null
  refetch: () => unknown
  /** The previous selection's result, still shown while the new one loads. */
  isPlaceholderData?: boolean
}

/**
 * One dashboard section: title, optional subtitle, info icon and actions, and its own loading,
 * error and empty states, so a failing endpoint only blanks its own section. While a new
 * selection loads, the previous figures stay visible but dimmed, marked busy and inert — they
 * never pass for the current selection's, and can't open a drilldown that wouldn't match them.
 *
 * `frame="card"` draws the section as one white card; `frame="open"` puts the heading on the
 * page background and leaves the content (its own cards) unframed. `hideTitle` keeps the
 * heading for screen readers only.
 */
export function SectionCard({
  title,
  subtitle,
  definition,
  actions,
  query,
  isEmpty = false,
  emptyText = 'No data for the selected period and filters.',
  frame = 'card',
  hideTitle = false,
  children,
}: {
  title: string
  subtitle?: ReactNode
  definition?: string
  actions?: ReactNode
  query: QueryState
  isEmpty?: boolean
  emptyText?: string
  frame?: 'card' | 'open'
  hideTitle?: boolean
  children: ReactNode
}) {
  const { isPending, error, refetch } = query
  const stale = query.isPlaceholderData === true

  useEffect(() => {
    if (error) logError('population', `Load ${title}`, error)
  }, [error, title])

  const heading = (
    <div
      className={
        hideTitle ? 'sr-only' : 'mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2'
      }
    >
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <h2 className={frame === 'open' ? 'text-lg font-semibold' : 'text-base font-semibold'}>
            {title}
          </h2>
          {definition && <InfoIcon label={title} definition={definition} />}
          {stale && <Loader size="xs" ml={4} aria-label={`Updating ${title}`} />}
        </div>
        {subtitle && <div className="mt-0.5 text-sm text-slate-500">{subtitle}</div>}
      </div>
      {actions}
    </div>
  )

  const body = isPending ? (
    <div className="flex justify-center py-8">
      <Loader size="sm" aria-label={`Loading ${title}`} />
    </div>
  ) : error ? (
    <Alert color="red" variant="light" title={`Couldn’t load ${title.toLowerCase()}`}>
      <Button size="xs" variant="light" color="red" onClick={() => refetch()}>
        Try again
      </Button>
    </Alert>
  ) : isEmpty ? (
    <p className="py-6 text-center text-sm text-slate-500">{emptyText}</p>
  ) : (
    <div
      aria-busy={stale}
      inert={stale}
      className={stale ? 'pointer-events-none opacity-50 transition-opacity' : undefined}
    >
      {children}
    </div>
  )

  if (frame === 'open') {
    // Loading, error and empty states still get a card, so the section keeps its place.
    const framedBody =
      isPending || error || isEmpty ? (
        <Paper withBorder radius="lg" p="md">
          {body}
        </Paper>
      ) : (
        body
      )
    return (
      <section aria-label={title}>
        {heading}
        {framedBody}
      </section>
    )
  }

  return (
    <Paper withBorder radius="lg" p="lg" component="section" aria-label={title}>
      {heading}
      {body}
    </Paper>
  )
}
